import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandMark from "@/components/BrandMark";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/applications", label: "Applications" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-gray-100"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="flex items-center group -my-3"
          >
            <BrandMark className="h-20 md:h-24 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors pm-link ${
                    isActive ? "text-brand" : "text-gray-700 hover:text-brand"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <Button
              asChild
              data-testid="nav-cta-request-sample"
              className="bg-brand hover:bg-brand-hover text-white rounded-sm px-5 h-10 font-medium"
            >
              <Link to="/contact">Request a Sample</Link>
            </Button>
          </div>

          <button
            className="md:hidden p-2 text-gray-800"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-gray-100 bg-white" data-testid="nav-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-gray-100 ${
                    isActive ? "text-brand" : "text-gray-800"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta"
              className="mt-3 inline-flex items-center justify-center bg-brand text-white py-3 rounded-sm font-medium"
            >
              Request a Sample
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
